import NiceModal, { useModal } from '@ebay/nice-modal-react';

import { useCallback, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Button from 'components/field/Button';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Image from 'components/global/Image';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import OTPModal from 'pages/OTP/OTPModal';

import { PROFILE, USERS } from 'services/consts';

import { setLoading } from 'stores/layoutSlice';

import AppLogger from 'utils/AppLogger';
import PATHS from 'utils/paths';

import { faTrashCan } from '@fortawesome/pro-regular-svg-icons';

import * as S from './GeneralSettings.styled';

export const AccountRemovalModal = NiceModal.create(() => {
	// Hooks and states declaration
	const { t } = useTranslation();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const axiosPrivate = useAxiosPrivate();
	const { showError, showSuccess, showWarning } = useAlert();
	const modal = useModal();
	const token = useRef(null);
	const isHandleVerifyOTPPending = useRef(false);

	const [isLoading, setIsLoading] = useState(false);

	const { mobileVerified, companyAdminVerified, phone } = useSelector((state) => state?.user);

	const handleCloseModal = useCallback(() => {
		modal.remove();
	}, []);

	const handleVerifyOTP = useCallback(
		async (modal, code) => {
			if (isHandleVerifyOTPPending.current) {
				return;
			}
			isHandleVerifyOTPPending.current = true;
			setIsLoading(true);

			await axiosPrivate
				.post(USERS.DELETE_ACCOUNT, {
					code,
					token: token.current
				})
				.then((response) => {
					if (response.status === 200) {
						handleCloseModal();
						modal.remove();
						showSuccess({
							message: 'accountRemoval.successMessage'
						});
						navigate(PATHS.LOGOUT);
					} else {
						AppLogger.error(response);
						showError();
					}
				})
				.catch((error) => {
					AppLogger.error(error);
					if (error?.response?.data?.code) {
						showError({ message: error.response.data.code });
					} else {
						showError();
					}
				});

			setIsLoading(false);
			isHandleVerifyOTPPending.current = false;
		},
		[showError, token.current, showSuccess, axiosPrivate]
	);

	const handleAccountRemovalInfo = useCallback(() => {
		if (!mobileVerified) {
			showWarning({
				header: 'accountRemoval.verifyMobileNumberHeader',
				message: 'accountRemoval.verifyMobileNumberMessage'
			});
			navigate(PATHS.USER_SETTINGS);
		} else if (!companyAdminVerified) {
			showError({
				header: 'changeBankInformation.verifyCompanyAdminMobileHeader',
				message: 'changeBankInformation.verifyCompanyAdminMobileMessage'
			});
		} else {
			sendCode();
		}
	}, [mobileVerified, companyAdminVerified, navigate, showWarning, showError]);

	const sendCode = useCallback(async () => {
		dispatch(setLoading(true));

		await axiosPrivate
			.post(PROFILE.REQUEST_MOBILE_OWNERSHIP_VERIFICATION, {
				otpType: 'ownership'
			})
			.then(async (response) => {
				if (response.status === 200) {
					showSuccess({ message: 'mobileNumber.codeSended' });
					if (response.data?.token) {
						token.current = response.data.token;
						dispatch(setLoading(false));
						await NiceModal.show(OTPModal, {
							data: phone,
							sendOTPCode: sendCode,
							verifyOTP: handleVerifyOTP,
							loadingVerify: isLoading,
							isMail: false
						});
						return response.data.token;
					} else {
						showError({ message: 'mobileNumber.cantVerified' });
						AppLogger.error('General Settings: Token was not come from the server');
						return false;
					}
				} else {
					showError({ message: 'mobileNumber.cantVerified' });
					AppLogger.error(`General Settings: Error while sending verify code: ${response}`);
					return false;
				}
			})
			.catch((error) => {
				if (error?.response?.data?.code === 'error.B008') {
					showError({
						header: 'error.B008',
						message: 'errorMsg.telephoneNumberValidation'
					});
				} else if (error?.response?.data?.code === 'error.B009') {
					showError({
						message: t('error.B009', {
							minutes: 2
						})
					});
				} else if (error?.response?.data?.code === 'error.EM02') {
					showError({
						message: 'error.EM02'
					});
				} else {
					showError();
					AppLogger.error(`General Settings: Error while sending verify code: ${error}`);
				}
				return false;
			});
		dispatch(setLoading(false));
	}, [showError, showSuccess, phone, t, handleVerifyOTP, dispatch, axiosPrivate]);

	return (
		<Modal fullwidth maxWidth="small" className="modal-account-removal">
			<ModalTitle>
				<Typography variant="p1">accountRemoval</Typography>
			</ModalTitle>
			<ModalContent>
				<Form borderless>
					<S.AccountRemovalContainer className="mt-3">
						<Image
							url={'https://storage.googleapis.com/tryoto-public/public/assets/images/delete-account.svg'}
							width="110px"
							height="110px"
						/>

						<Typography fontWeight="500" variant="p1" className="mt-4 mb-3" noWrap>
							accountRemovalQuestion
						</Typography>

						<FormItem>
							<Typography fontWeight="400" className="mt-4 mb-3 mx-5" variant="p3" noWrap>
								<Trans i18nKey={`accountRemovalContent`} />
							</Typography>
						</FormItem>
					</S.AccountRemovalContainer>

					{/* <FormItem>
						<F.Line className="line" />
					</FormItem> */}

					{/* <FormItem label="mobileNumber.accountRemoval" isLoading={isLoading} required>
						<OTPVerification
							name="phone"
							className="mx-5"
							onVerification={(value) => {
								setValue('phone', value, { shouldValidate: true, shouldDirty: true });
								value;
								dispatch(
									setUserDetails({
										phone: value,
										mobileVerified: true,
										...(user.userType === 'companyAdmin' && { companyAdminVerified: true })
									})
								);
								update({ phone: value });
							}}
							type="phone"
						/>
					</FormItem> */}
				</Form>
			</ModalContent>
			<ModalActions>
				<Button
					label="cancel"
					onClick={() => {
						modal.remove();
					}}
					variant="borderless"
				/>

				<Button
					icon={faTrashCan}
					variant="solid"
					color="var(--red)"
					label="deleteAccount"
					loading={isLoading}
					onClick={handleAccountRemovalInfo}
				/>
			</ModalActions>
		</Modal>
	);
});
export default AccountRemovalModal;
